import { useMemo } from 'react';

/**
 * CRPD-7209 de-mock — the cost accessor layer, now reading the real generated
 * Control fields (CRPD-7222 schema). This replaces the deleted `src/api/costMocks/`
 * layer and stays the single seam between query data and the cost surfaces.
 *
 * Note: the BE collapsed the two spec-§3.1 notes to a single `costNote` ↔ `cost_note`
 * (Tim's call, 2026-07-23) — the one-vs-two-notes question is settled; the FE's single
 * "Cost Note" now maps 1:1.
 */

/** Cost fields as consumed by every display surface. */
export interface ControlCostFields {
  /** Evaluated (when annualCostFormula is set) or literal annual cost. */
  annualCost: number | null;
  annualCostFormula: string | null;
  /** Evaluated (when implementationCostFormula is set) or literal implementation cost. */
  implementationCost: number | null;
  implementationCostFormula: string | null;
  costNote: string | null;
}

/** Stored (input) shape the edit modal round-trips — same fields, literal semantics. */
export type ControlCostInputFields = ControlCostFields;

export interface FrameworkControlCostFields {
  annualCostFormula: string | null;
  implementationCostFormula: string | null;
  costNote: string | null;
}

/** Structural slice of a generated Control any cost surface needs. */
export interface ControlCostSource {
  id: string;
  annualCost?: number | null;
  annualCostFormula?: string | null;
  costNote?: string | null;
  implementationCost?: number | null;
  implementationCostFormula?: string | null;
}

export const toControlCostFields = (control: ControlCostSource): ControlCostFields => ({
  annualCost: control.annualCost ?? null,
  annualCostFormula: control.annualCostFormula ?? null,
  implementationCost: control.implementationCost ?? null,
  implementationCostFormula: control.implementationCostFormula ?? null,
  costNote: control.costNote ?? null,
});

export const useControlCostFields = (control: ControlCostSource | null | undefined): ControlCostFields | null => {
  return useMemo(() => (control ? toControlCostFields(control) : null), [control]);
};

/** Bulk variant for tables — one call at page level, cells receive fields via props. */
export const useControlsCostFields = (controls: ControlCostSource[]): Map<string, ControlCostFields> => {
  return useMemo(() => {
    const result = new Map<string, ControlCostFields>();
    controls.forEach((control) => result.set(control.id, toControlCostFields(control)));
    return result;
  }, [controls]);
};

/** Structural slice of a generated FrameworkControl carrying the template cost columns (CRPD-7222 WS-1). */
export interface FrameworkControlCostSource {
  id: string;
  annualCostFormula?: string | null;
  implementationCostFormula?: string | null;
  costNote?: string | null;
}

export const toFrameworkControlCostFields = (fc: FrameworkControlCostSource): FrameworkControlCostFields => ({
  annualCostFormula: fc.annualCostFormula ?? null,
  implementationCostFormula: fc.implementationCostFormula ?? null,
  costNote: fc.costNote ?? null,
});

/** Template cost fields for one framework control — sourced from the row's real generated fields. */
export const useFrameworkControlCostFields = (
  frameworkControl: FrameworkControlCostSource | null | undefined,
): FrameworkControlCostFields | null => {
  return useMemo(() => (frameworkControl ? toFrameworkControlCostFields(frameworkControl) : null), [frameworkControl]);
};

/** Bulk variant for library tables — one call at page level, keyed by framework-control id. */
export const useFrameworkControlsCostFields = (
  frameworkControls: FrameworkControlCostSource[],
): Map<string, FrameworkControlCostFields> => {
  return useMemo(() => {
    const result = new Map<string, FrameworkControlCostFields>();
    frameworkControls.forEach((fc) => result.set(fc.id, toFrameworkControlCostFields(fc)));
    return result;
  }, [frameworkControls]);
};
